import { useState, type ComponentType } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ChevronDown, Menu, X } from 'lucide-react'
import logo from '@/assets/brand/logo.png'
import { UserAvatarMenu } from '@/platform/session/UserAvatarMenu'

export interface ShellNavigationSubItem {
  id: string
  path: string
  label: string
  Icon?: ComponentType<{ size?: number; className?: string }>
}

export interface ShellNavigationItem {
  id: string
  path: string
  label: string
  Icon: ComponentType<{ size?: number; className?: string }>
  children?: ShellNavigationSubItem[]
}


interface AppShellProps {
  homePath: string
  navigation: ShellNavigationItem[]
}

export function AppShell({ homePath, navigation }: AppShellProps) {
  const [isNavigationOpen, setNavigationOpen] = useState(false)
  const closeNavigation = () => setNavigationOpen(false)
  const location = useLocation()

  return (
    <div className="shell">
      <header className="shell-header">
        <button className="menu-toggle" type="button" onClick={() => setNavigationOpen(true)} aria-label="Mở menu"><Menu size={20} /></button>
        <Brand homePath={homePath} compact />
        <strong className="shell-title">Biên Soạn Học Nhé</strong>
        <UserAvatarMenu />
      </header>
      {isNavigationOpen && <button className="shell-backdrop" type="button" aria-label="Đóng menu" onClick={closeNavigation} />}
      <aside className={`shell-nav ${isNavigationOpen ? 'open' : ''}`} aria-label="Điều hướng biên soạn">
        <div className="shell-brand-row"><Brand homePath={homePath} /><button className="close-menu" type="button" onClick={closeNavigation} aria-label="Đóng menu"><X size={20} /></button></div>
        <nav className="shell-nav-list">
          {navigation.map((item) => {
            if (item.children && item.children.length > 0) {
              const isGroupActive = location.pathname.startsWith(`/${item.id}`)
              return (
                <div key={item.id} className="flex flex-col gap-1">
                  <NavLink
                    to={item.path}
                    onClick={closeNavigation}
                    className={({ isActive }) =>
                      `nav-item ${isActive || isGroupActive ? 'active' : ''}`
                    }
                  >
                    <item.Icon size={19} />
                    <span className="flex-1">{item.label}</span>
                    <ChevronDown
                      size={14}
                      className={`text-slate-400 transition-transform duration-200 ${
                        isGroupActive ? 'rotate-180' : ''
                      }`}
                    />
                  </NavLink>
                  {isGroupActive && (
                    <div className="ml-5 flex flex-col gap-0.5 border-l border-slate-200 pl-2">
                      {item.children.map((sub) => {
                        const SubIcon = sub.Icon
                        return (
                          <NavLink
                            key={sub.id}
                            to={sub.path}
                            onClick={closeNavigation}
                            className={({ isActive }) =>
                              `flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                                isActive
                                  ? 'bg-blue-50 text-blue-600 font-bold'
                                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                              }`
                            }
                          >
                            {SubIcon && <SubIcon size={14} className="shrink-0" />}
                            <span>{sub.label}</span>
                          </NavLink>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            return (
              <NavLink
                key={item.id}
                to={item.path}
                onClick={closeNavigation}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <item.Icon size={19} />
                <span>{item.label}</span>
              </NavLink>
            )
          })}
        </nav>
      </aside>
      <main className="shell-main"><Outlet /></main>
    </div>
  )
}


function Brand({ homePath, compact = false }: { homePath: string; compact?: boolean }) {
  return <NavLink to={homePath} className={`brand ${compact ? 'compact' : ''}`}>
    <img src={logo} alt="Học Nhé" />
    <span><b>Học Nhé</b><small>Học cùng nhau</small></span>
  </NavLink>
}
